import React, { useEffect, useMemo, useState } from "react";
import { FileText, Mail } from "lucide-react";
import api, { formatApiError, downloadFromApi } from "../lib/api";
import Modal from "./Modal";

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function PackingListsModal({ order, customer, onClose, onGenerated }) {
    const [rpEntries, setRpEntries] = useState([]);
    const [selectedRpIds, setSelectedRpIds] = useState(new Set());
    const [shipDate, setShipDate] = useState(todayIso());
    const [notes, setNotes] = useState("");
    const [queueEmail, setQueueEmail] = useState(false);
    const [emailOverride, setEmailOverride] = useState("");
    const [markCompleted, setMarkCompleted] = useState(false);
    const [history, setHistory] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get(`/orders/${order.id}/rp`).then((r) => {
            setRpEntries(r.data);
            setSelectedRpIds(new Set(r.data.map((rp) => rp.id)));
        });
        api.get(`/orders/${order.id}/packing-lists`).then((r) => setHistory(r.data));
    }, [order.id]);

    const toggleRp = (id) =>
        setSelectedRpIds((prev) => {
            const s = new Set(prev);
            if (s.has(id)) s.delete(id);
            else s.add(id);
            return s;
        });

    const summary = useMemo(() => {
        let pallets = 0;
        let units = 0;
        for (const rp of rpEntries) {
            if (!selectedRpIds.has(rp.id)) continue;
            pallets += rp.total_pallets;
            units += rp.total_units;
        }
        return { pallets, units };
    }, [rpEntries, selectedRpIds]);

    const resolvedRecipients = useMemo(() => {
        if (emailOverride.trim()) {
            return emailOverride
                .split(/[,;\s]+/)
                .map((s) => s.trim())
                .filter((s) => s.includes("@"));
        }
        if (!customer) return [];
        const contacts = customer.contacts || [];
        const primary = contacts.find((c) => c.is_primary && c.email);
        if (primary?.email) return [primary.email];
        return contacts.filter((c) => c.email).map((c) => c.email);
    }, [customer, emailOverride]);

    const submit = async () => {
        setError("");
        setBusy(true);
        try {
            if (rpEntries.length === 0) {
                throw new Error("No RP entries found — record production before generating a Packing List.");
            }
            if (selectedRpIds.size === 0) {
                throw new Error("Select at least one RP entry.");
            }
            if (queueEmail && resolvedRecipients.length === 0) {
                throw new Error(
                    "No recipient email available. Add a contact with email or override below.",
                );
            }
            const payload = {
                ship_date: shipDate,
                rp_ids: Array.from(selectedRpIds),
                notes,
                queue_email: queueEmail,
                email_to_override: emailOverride.trim() ? resolvedRecipients : null,
                mark_completed: markCompleted,
            };
            const { data: doc } = await api.post(
                `/orders/${order.id}/packing-lists`,
                payload,
            );
            // Download the newly generated PL PDF
            try {
                await downloadFromApi(`/documents/${doc.id}/download`, doc.filename);
            } catch (dlErr) {
                console.error("PL post-generation download failed", dlErr);
            }
            onGenerated?.(doc);
        } catch (err) {
            const msg = formatApiError(err?.response?.data?.detail) || err.message;
            setError(msg);
        } finally {
            setBusy(false);
        }
    };

    const reprint = async (doc) => {
        try {
            await downloadFromApi(`/documents/${doc.id}/reprint`, doc.filename);
        } catch (err) {
            console.error("PL reprint failed", err);
            setError(formatApiError(err?.response?.data?.detail) || err.message);
        }
    };

    return (
        <Modal
            title={`Packing List · ${order.internal_number}`}
            onClose={onClose}
            size="lg"
            testId="pl-modal"
        >
            <div className="space-y-4">
                {rpEntries.length === 0 ? (
                    <div className="text-sm border border-amber-200 bg-amber-50 text-amber-900 rounded-sm px-3 py-2">
                        No RP entries found — record production before generating a
                        Packing List.
                    </div>
                ) : (
                    <>
                        <div className="text-xs text-gray-600">
                            Select RP entries to include in this Packing List.
                        </div>
                        <div className="border border-gray-200 rounded-sm overflow-hidden">
                            <table className="odr-table">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>RP #</th>
                                        <th>Date</th>
                                        <th>Product</th>
                                        <th className="text-right">Pallets</th>
                                        <th className="text-right">Units</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rpEntries.map((rp) => (
                                        <tr key={rp.id}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedRpIds.has(rp.id)}
                                                    onChange={() => toggleRp(rp.id)}
                                                    data-testid={`pl-rp-toggle-${rp.rp_number}`}
                                                />
                                            </td>
                                            <td className="odr-mono text-xs">{rp.rp_number}</td>
                                            <td className="text-xs">{rp.production_date}</td>
                                            <td className="text-xs">{rp.product_name_snapshot}</td>
                                            <td className="text-right odr-mono">
                                                {rp.total_pallets}
                                            </td>
                                            <td className="text-right odr-mono">
                                                {Number(rp.total_units).toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-sm px-3 py-2 flex flex-wrap gap-4">
                            <span>
                                Pallets:{" "}
                                <span className="font-semibold odr-mono">{summary.pallets}</span>
                            </span>
                            <span>
                                Units:{" "}
                                <span className="font-semibold odr-mono">
                                    {summary.units.toLocaleString()}
                                </span>
                            </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                                    Ship Date
                                </label>
                                <input
                                    type="date"
                                    value={shipDate}
                                    onChange={(e) => setShipDate(e.target.value)}
                                    className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm"
                                    data-testid="pl-ship-date"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                                    Notes (optional)
                                </label>
                                <input
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Free-text shipping notes"
                                    className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm"
                                    data-testid="pl-notes"
                                />
                            </div>
                        </div>
                        <div className="border border-gray-200 rounded-sm p-3 space-y-2">
                            <label className="flex items-center gap-2 text-sm text-gray-800">
                                <input
                                    type="checkbox"
                                    checked={queueEmail}
                                    onChange={(e) => setQueueEmail(e.target.checked)}
                                    data-testid="pl-queue-email"
                                />
                                <Mail className="w-4 h-4 text-gray-500" />
                                Queue email to customer (saved to outbox — provider not yet
                                wired)
                            </label>
                            {queueEmail && (
                                <>
                                    <input
                                        value={emailOverride}
                                        onChange={(e) => setEmailOverride(e.target.value)}
                                        placeholder="Override recipients (comma-separated) — leave blank to use customer contacts"
                                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm"
                                        data-testid="pl-email-override"
                                    />
                                    <div className="text-xs text-gray-600">
                                        Will queue to:{" "}
                                        <span className="odr-mono">
                                            {resolvedRecipients.length
                                                ? resolvedRecipients.join(", ")
                                                : "— none resolved —"}
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="border border-gray-200 rounded-sm p-3 space-y-2">
                            <label className="flex items-center gap-2 text-sm text-gray-800">
                                <input
                                    type="checkbox"
                                    checked={markCompleted}
                                    onChange={(e) => setMarkCompleted(e.target.checked)}
                                    data-testid="pl-mark-completed"
                                />
                                <span className="font-semibold">Mark Order as Completed</span>
                            </label>
                            <div className="text-xs text-gray-600">
                                When checked, the order will transition to{" "}
                                <span className="odr-mono">completed</span> after the PL is
                                generated. Tolerance and production totals are re-validated
                                server-side. Standalone &ldquo;Complete Order&rdquo; button is still
                                available on the Order Detail page.
                            </div>
                        </div>
                    </>
                )}

                {history.length > 0 && (
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wider text-gray-600 mb-2">
                            Version History
                        </div>
                        <div className="border border-gray-200 rounded-sm overflow-hidden">
                            <table className="odr-table">
                                <thead>
                                    <tr>
                                        <th>PL #</th>
                                        <th>Version</th>
                                        <th>Generated</th>
                                        <th>By</th>
                                        <th className="text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map((d) => (
                                        <tr key={d.id}>
                                            <td className="odr-mono text-xs">
                                                {d.document_number}
                                            </td>
                                            <td className="odr-mono">v{d.version}</td>
                                            <td className="text-xs">
                                                {new Date(d.generated_at).toLocaleString()}
                                            </td>
                                            <td className="text-xs">{d.generated_by}</td>
                                            <td className="text-right">
                                                <button
                                                    onClick={() => reprint(d)}
                                                    className="text-sm text-gray-700 hover:text-gray-900 font-semibold"
                                                    data-testid={`pl-reprint-${d.document_number}`}
                                                >
                                                    Reprint
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2">
                        {error}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="odr-btn rounded-sm border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50"
                        data-testid="pl-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={submit}
                        disabled={busy || rpEntries.length === 0}
                        className="odr-btn rounded-sm bg-[var(--odr-red)] hover:bg-[var(--odr-red-hover)] text-white text-sm font-semibold px-4 disabled:opacity-60 inline-flex items-center gap-1"
                        data-testid="pl-submit"
                    >
                        <FileText className="w-4 h-4" />
                        {busy
                            ? "Generating…"
                            : history.length > 0
                              ? `Generate V${history.length + 1}`
                              : "Generate V1"}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
